import { Trans, useTranslation } from 'react-i18next';
import React, { useRef, useCallback, useMemo, useEffect } from 'react';
import produce from 'immer';
import cx from 'clsx';
import { useForm, useWatch } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';

import { Tips, UserSelectField } from '@/components';
import { NumberInputField } from '@/components/common';
import { CommonDependency } from '@/dependency';
import { Channel, RichTextEditor, RichTextEditorHandle } from '@/components/RichTextEditor';
import { useSwitch, useChannelPick } from '@/hooks';
import { devLog, formatNum } from '@/utils';
import { SUPPORTED_LOCALE_TYPE } from '@/locales';

import { PostFooter } from '../PostFooter';
import style from './index.module.scss';

export type QuestionProps = {
  onConfirm: (data: EditorSubmitContentData) => Promise<void>;
  disabled?: boolean;
  extraPaymentParams?: Object;
  className?: string;
  targetAnswerer?: UserInfo;
  priceLimit?: number[];
  minOfferPriceRatio?: number;
  language?: SUPPORTED_LOCALE_TYPE;
  isDebug?: boolean;
  channel?: Channel;
  hideChannelPicker?: boolean;
};

export const Question = ({
  onConfirm,
  disabled = false,
  extraPaymentParams,
  className,
  targetAnswerer,
  priceLimit = [1, 100],
  minOfferPriceRatio = 10000,
  language,
  isDebug,
  channel,
  hideChannelPicker,
}: QuestionProps) => {
  const { t } = useTranslation();

  const PayModal = CommonDependency.components.PayModal;

  const editorRef = useRef<RichTextEditorHandle>(null);

  const restDrawData = useRef<EditorSubmitContentData>();
  const { on: isLoading, turn: setLoading } = useSwitch();
  const { on: isL2Pay, turnOn: showL2Pay, turnOff: hideL2Pay } = useSwitch();
  const { on: isEditorInValid, turn: setInvalid } = useSwitch();
  const { channelId } = useChannelPick(channel);

  const {
    handleSubmit,
    control,
    formState: { isValid, errors },
    trigger,
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      answerer: targetAnswerer,
    } as any,
  });

  useEffect(() => {
    if (targetAnswerer) {
      trigger('answerer');
    }
  }, [targetAnswerer, trigger]);

  const [price, answerer] = useWatch({ control, name: ['offer_price', 'answerer'] });

  const txParams = useMemo(
    () =>
      price
        ? {
            price: Math.round(+price * 10000),
            message: `Pay $${formatNum(price)} for AskPro question`,
            ...extraPaymentParams,
          }
        : undefined,
    [price, extraPaymentParams],
  );

  const payTitle = useMemo(
    () => (
      <Trans i18nKey="Qa_Question.payTitle" values={{ price: `$${formatNum(price)}` }}>
        Pay
        <span className={style.payTitleWeight}>$0</span>
        for AskPro question
      </Trans>
    ),
    [price],
  );

  const onSubmit = useCallback(
    async data => {
      try {
        setLoading(true);
        const params: EditorSubmitContentData = {
          type: 'pay_qa',
          content: '',
          pay_qa_data: {
            offer_price: data.offer_price * 10000,
            answerer_id: data.answerer?.id,
          },
          permissions: {},
          channel_id: channelId,
        };

        const textData = await editorRef.current?.getSubmitContent();

        if (textData) {
          Object.assign(params, {
            content: textData.textContent,
            entities: {
              ...(textData.mentionsList?.length ? { mentions: textData.mentionsList } : {}),
              ...(textData.hashtagsList?.length ? { tags: textData.hashtagsList } : {}),
              ...(textData.urlsWidthIndices?.length ? { url: textData.urlsWidthIndices } : {}),
              ...(textData.slashesList.length ? { channels: textData.slashesList } : {}),
            },
          });
        }

        devLog('submit qa question content', params);

        restDrawData.current = params;
        showL2Pay();
      } catch (error) {
        setLoading(false);
      }
    },
    [setLoading, showL2Pay, channelId],
  );

  const handleL2Pay = useCallback(
    async (txData: Object) => {
      if (restDrawData.current) {
        const params = produce(restDrawData.current, draft => {
          draft = {
            ...draft,
            ...txData,
          };
          return draft;
        });
        await onConfirm(params);
        setLoading(false);
      }
    },
    [onConfirm, setLoading],
  );

  const handleL2PayHide = useCallback(() => {
    hideL2Pay();
    setLoading(false);
  }, [hideL2Pay, setLoading]);

  const _priceLimit = useMemo(() => {
    return priceLimit.map(x => formatNum(x));
  }, [priceLimit]);

  const minOfferPrice = useMemo(() => {
    return Math.min(
      Math.max(
        answerer?.pay_qa_offer_price
          ? Math.ceil(answerer?.pay_qa_offer_price / 10000)
          : Math.ceil(answerer?.desc?.usd_value / minOfferPriceRatio) || 0,
        priceLimit[0],
      ),
      priceLimit[1],
    );
  }, [answerer, minOfferPriceRatio, priceLimit]);

  const formatedMinOffierPrice = useMemo(() => `${formatNum(minOfferPrice, 2)}`, [minOfferPrice]);

  return (
    <>
      <div className={cx(style.questionContainer, className)}>
        <div className={style.main}>
          <div className={style.tip}>{t('Qa_Question.4i5z3v', { price: '$1' })}</div>
          <div className={style.field}>
            <div className={style.fieldTitle}>{t('Qa_Question.wl2u1u')}</div>
            <div className={style.fieldValue}>
              <UserSelectField
                name="answerer"
                control={control}
                disabled={disabled}
                inputWidth={300}
                defaultUserInfo={targetAnswerer}
                placeholder={t('Qa_Question.kqyp7h')}
                className={style.answererInput}
                rules={{
                  required: true,
                  validate: x => {
                    if (isDebug) {
                      return true;
                    }

                    if (!x?.enable_pay_qa) {
                      return t('Qa_Question.answerEnableError');
                    }

                    return true;
                  },
                }}
              />
              <ErrorMessage
                errors={errors}
                name="answerer"
                render={({ message }) => {
                  return <div className={style.errorMessage}>{message}</div>;
                }}
              />
            </div>
          </div>
          <div className={style.field}>
            <div className={style.fieldTitle}>{t('Qa_Question.92hd1h')}</div>
            <div className={style.fieldValue}>
              <NumberInputField
                inputWidth={160}
                placeholder={
                  minOfferPrice === priceLimit[1]
                    ? _priceLimit[1]
                    : t('Qa_Question.offerPriceNoLess', { price: formatedMinOffierPrice })
                }
                inputClassName={style.offerPriceInput}
                rules={{
                  required: true,
                  max: {
                    value: priceLimit[1],
                    message: t('Qa_Question.priceMax', { price: `$${_priceLimit[1]}` }),
                  },
                  pattern: {
                    value: /^\d+$/,
                    message: t('Qa_Question.1jffb0s'),
                  },
                  // min validate
                  validate: (v: number) => {
                    if (isDebug) {
                      return true;
                    }

                    if (v >= minOfferPrice) {
                      return true;
                    }

                    if (minOfferPrice >= priceLimit[0]) {
                      return t('Qa_Question.minPrice', { price: formatedMinOffierPrice });
                    }

                    return t('Qa_Question.minPriceCommon', { price: formatedMinOffierPrice });
                  },
                }}
                name="offer_price"
                control={control}
                disabled={disabled}
                prefixStr="$"
              />
              <Tips
                className={style.priceTip}
                placement="right"
                title={
                  <>
                    {minOfferPrice === priceLimit[1]
                      ? t('Qa_Question.respondentPrice', { price: _priceLimit[1] })
                      : t('Qa_Question.priceBlock', {
                          min: formatedMinOffierPrice,
                          max: _priceLimit[1],
                        })}
                    <br />
                    {t('Qa_Question.7lql43')}
                  </>
                }
              />
              <ErrorMessage
                errors={errors}
                name="offer_price"
                render={({ message }) => {
                  return <div className={style.errorMessage}>{message}</div>;
                }}
              />
            </div>
          </div>
          <RichTextEditor
            maxLength={600}
            editorClassName={style.textEditor}
            placeholderClassName={style.editorStyle}
            wrapperClassName={style.editorWrapper}
            footer={false}
            disableUploadImage
            placeholder={t('Qa_Question.1vfp5by')}
            disabled={disabled}
            disableCard
            onInvalidChange={setInvalid}
            ref={editorRef}
            language={language}
          />
        </div>
        <PostFooter
          disabled={disabled || isEditorInValid || !isValid}
          isLoading={isLoading}
          onSubmit={handleSubmit(onSubmit)}
          showPaidEligibility
          hideChannelPicker={hideChannelPicker}
        />
      </div>
      {isL2Pay ? (
        <PayModal
          turnOff={handleL2PayHide}
          txParams={txParams}
          payTitle={payTitle}
          onPaySubmit={handleL2Pay}
        />
      ) : null}
    </>
  );
};
